import React, { useEffect, useState } from "react";
import axios from "axios";
import { useAuth } from "../AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, CheckCircle2, XCircle } from "lucide-react";

/**
 * QuizBuilder — teacher-side editor for a video's quiz.
 * QuizTaker — learner-side, submits answers and displays score.
 */

export function QuizBuilder({ videoId, videoTitle, onSaved }) {
  const { API } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [title, setTitle] = useState("Quiz");
  const [passScore, setPassScore] = useState(70);
  const [questions, setQuestions] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/quizzes/video/${videoId}`, { withCredentials: true }).then((r) => {
      if (r.data) {
        setQuiz(r.data);
        setTitle(r.data.title);
        setPassScore(r.data.pass_score);
        setQuestions(r.data.questions.map((q) => ({ ...q })));
      } else {
        setQuestions([{ text: "", options: ["", ""], correct_index: 0 }]);
      }
    });
  }, [videoId, open]);

  const addQuestion = () => setQuestions([...questions, { text: "", options: ["", ""], correct_index: 0 }]);
  const removeQuestion = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const setQ = (i, patch) => setQuestions(questions.map((q, idx) => idx === i ? { ...q, ...patch } : q));

  const save = async () => {
    if (!questions.length) return toast.error("Add at least one question");
    for (const q of questions) {
      if (!q.text || q.options.length < 2 || q.options.some((o) => !o.trim())) {
        return toast.error("Fill all questions and options");
      }
    }
    try {
      await axios.post(`${API}/quizzes`, {
        video_id: videoId, title, pass_score: parseInt(passScore), questions,
      }, { withCredentials: true });
      toast.success("Quiz saved");
      setOpen(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="text-xs text-[#D45D3C] hover:underline" data-testid={`edit-quiz-${videoId}`}>
        {quiz ? "Edit quiz" : "Add quiz"}
      </button>
    );
  }

  return (
    <div className="mt-3 card-surface p-4 bg-[#F6F5F2]" data-testid={`quiz-builder-${videoId}`}>
      <div className="overline mb-2">Quiz for: {videoTitle}</div>
      <div className="grid md:grid-cols-3 gap-3 mb-3">
        <label>
          <div className="overline mb-1">Quiz title</div>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <label>
          <div className="overline mb-1">Pass score (%)</div>
          <input className="input" type="number" value={passScore} onChange={(e) => setPassScore(e.target.value)} />
        </label>
      </div>

      {questions.map((q, i) => (
        <div key={i} className="border border-[#E5E3D8] rounded p-3 mb-3 bg-white">
          <div className="flex items-center justify-between mb-2">
            <div className="overline">Question {i + 1}</div>
            <button onClick={() => removeQuestion(i)} className="text-[#C2482A]" data-testid={`remove-question-${i}`}>
              <Trash2 size={14} />
            </button>
          </div>
          <input className="input mb-2" placeholder="Question text"
                 value={q.text} onChange={(e) => setQ(i, { text: e.target.value })}
                 data-testid={`question-text-${i}`} />
          {q.options.map((o, oi) => (
            <div key={oi} className="flex items-center gap-2 mb-1.5">
              <input type="radio" name={`correct-${i}`} checked={q.correct_index === oi}
                     onChange={() => setQ(i, { correct_index: oi })}
                     data-testid={`option-correct-${i}-${oi}`} />
              <input className="input flex-1" placeholder={`Option ${oi + 1}`} value={o}
                     onChange={(e) => setQ(i, { options: q.options.map((x, ix) => ix === oi ? e.target.value : x) })}
                     data-testid={`option-text-${i}-${oi}`} />
              {q.options.length > 2 && (
                <button onClick={() => setQ(i, { options: q.options.filter((_, ix) => ix !== oi), correct_index: 0 })} className="text-[#C2482A]">
                  <XCircle size={14} />
                </button>
              )}
            </div>
          ))}
          <button onClick={() => setQ(i, { options: [...q.options, ""] })} className="text-xs text-[#D45D3C] mt-1">+ Add option</button>
        </div>
      ))}

      <div className="flex items-center gap-3">
        <button onClick={addQuestion} className="btn-outline text-sm flex items-center gap-1" data-testid="add-question-btn">
          <Plus size={14} /> Add question
        </button>
        <button onClick={save} className="btn-primary text-sm" data-testid="save-quiz-btn">Save quiz</button>
        <button onClick={() => setOpen(false)} className="text-sm text-[#5C5C5C]">Cancel</button>
      </div>
      <style>{`.input{width:100%;padding:0.55rem 0.8rem;border:1px solid #E5E3D8;border-radius:8px;background:white;outline:none;font-family:inherit;font-size:0.9rem}.input:focus{border-color:#0A231C}`}</style>
    </div>
  );
}

export function QuizTaker({ videoId, courseId, onPassed }) {
  const { API } = useAuth();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/quizzes/video/${videoId}`, { withCredentials: true }).then((r) => {
      setQuiz(r.data);
      if (r.data) setAnswers(new Array(r.data.questions.length).fill(-1));
    });
  }, [videoId]);

  if (!quiz) return null;

  const submit = async () => {
    if (answers.includes(-1)) return toast.error("Answer every question");
    try {
      const r = await axios.post(`${API}/quizzes/${quiz.id}/submit`, { answers }, { withCredentials: true });
      setResult(r.data);
      if (r.data.passed) {
        toast.success(`Passed with ${r.data.score}%`);
        onPassed?.();
      } else {
        toast.error(`Scored ${r.data.score}% — need ${quiz.pass_score}%`);
      }
    } catch (e) {
      toast.error("Submit failed");
    }
  };

  return (
    <div className="card-surface p-6 mt-4" data-testid={`quiz-taker-${videoId}`}>
      <div className="overline">Knowledge check</div>
      <div className="font-heading text-lg font-semibold mt-1">{quiz.title}</div>
      <div className="text-xs text-[#5C5C5C] mb-4">Pass score: {quiz.pass_score}%</div>

      {quiz.questions.map((q, i) => (
        <div key={i} className="mb-4">
          <div className="font-heading font-medium mb-2">{i + 1}. {q.text}</div>
          <div className="space-y-1.5">
            {q.options.map((o, oi) => (
              <label key={oi} className="flex items-center gap-2 text-sm cursor-pointer">
                <input type="radio" name={`q-${i}`} checked={answers[i] === oi}
                       onChange={() => setAnswers(answers.map((x, ix) => ix === i ? oi : x))}
                       data-testid={`quiz-answer-${i}-${oi}`} />
                {o}
              </label>
            ))}
          </div>
        </div>
      ))}

      {result ? (
        <div className={`p-3 rounded ${result.passed ? "bg-[#E4F0E5] text-[#2E7D32]" : "bg-[#FAE4DC] text-[#C2482A]"}`} data-testid="quiz-result">
          {result.passed ? (
            <div className="flex items-center gap-2"><CheckCircle2 size={16} /> You passed with {result.score}%</div>
          ) : (
            <div className="flex items-center gap-2"><XCircle size={16} /> You scored {result.score}%. Try again to pass.</div>
          )}
        </div>
      ) : (
        <button onClick={submit} className="btn-primary text-sm" data-testid="submit-quiz-btn">Submit answers</button>
      )}
      {result && !result.passed && (
        <button onClick={() => { setResult(null); setAnswers(new Array(quiz.questions.length).fill(-1)); }}
                className="btn-outline text-sm ml-2" data-testid="retry-quiz-btn">Try again</button>
      )}
    </div>
  );
}
